import React from 'react'
import PropTypes from 'prop-types'

import { Heading, FormErrorNotification, FormLabel, MultiSearch, Button } from '@offerzen/pinata-shame'

import ControlledHookField from '../../../CandidateExperience/components/ControlledHookField'
import CardFooterNav from '../CardFooterNav'
import { previousOnboardingUrl } from '../../helpers/onboardingSteps'
import styles from './styles'
import WorkAuthList from './WorkAuthList'
import WorkAuthItem from './WorkAuthList/WorkAuthItem'
import WorkAuthFields from './WorkAuthList/WorkAuthFields'

import { useCitizenship } from './hooks/useCitizenship'

const validators = {
  requiredSearchField: value => (value && value[0] && value[0].label ? undefined : 'Required'),
}

const CitizenshipStatusByCountryForm = props => {
  const { regionalCitizenships, history, location, reduxChangeUrl, nextUrl } = props

  const {
    countryListLast,
    countryListFirst,
    isLoadingCountries,
    isLoadingSubmitNewWorkAuth,
    selectedCountries,
    handleSubmit,
    errors,
    filteringCitizenshipCountries,
    isCreatingCitizenships,
    isUpdatingCitizenship,
    isDeletingCitizenship,
    validateCountry,
    onCitizenshipCountrySearch,
    onCitizenshipOptionChange,
    onCitizenshipTagRemove,
    formSubmitHandler,
    formChangeHandler,
    handleToggleEditing,
    handleToggleAdding,
    formError,
    control,
    filteredCitizenshipCountryResults,
    controlledFields,
    editingCitizenshipIds,
    allCountries,
    deleteCitizenship,
    submitUpdateForm,
    newCitizenship,
    createCitizenship,
    setNewCitizenship,
  } = useCitizenship({
    regionalCitizenships,
    reduxChangeUrl,
    nextUrl,
    candidateId: props.candidateId,
    preferredCountries: props.preferredCountries,
  })

  return (
    <form onSubmit={handleSubmit(formSubmitHandler)} onChange={formChangeHandler} className={styles.wrapper}>
      <If condition={formError}>
        <FormErrorNotification text={formError && formError.message} />
      </If>

      <Heading className={styles.heading}>What is your citizenship?</Heading>
      <p>Add the countries you are a citizen of. You can add one or multiple citizenships.</p>

      <FormLabel htmlFor="citizenships" label="Citizenship country" />
      <ControlledHookField
        id="citizenships"
        name="citizenships"
        theme="primary"
        control={control}
        className={styles.field}
        placeholder="Search for a country"
        component={MultiSearch}
        validate={[validators.requiredSearchField, onCitizenshipOptionChange]}
        onSearch={onCitizenshipCountrySearch}
        onChange={onCitizenshipOptionChange}
        onRemove={onCitizenshipTagRemove}
        fetching={isLoadingCountries || filteringCitizenshipCountries}
        results={filteredCitizenshipCountryResults.current}
        disabled={errors.citizenships && errors.citizenships.type === 'error-duplicate-country'}
      />

      <Heading className={styles.heading}>What is your work authorisation? (optional)</Heading>
      <p className={styles.countrySubheading}>
        Add your work authorisation for the countries you want to work in
        <Choose>
          <When condition={props.preferredCountries.length}>:</When>
          <Otherwise>.</Otherwise>
        </Choose>
      </p>
      <p>
        <b>{countryListFirst.join(', ')} </b>
        <If condition={props.preferredCountries.length > 1}>and</If>
        <b> {countryListLast}</b>
      </p>

      <p>This helps improve your chances of being matched with the right companies.</p>

      <WorkAuthList>
        <For each="regionalCitizenship" of={controlledFields} index="rcIndex">
          <WorkAuthItem
            selectedCountries={selectedCountries}
            key={regionalCitizenship.rcId}
            index={rcIndex}
            initialValues={regionalCitizenship}
            form={`citizenship-${regionalCitizenship.rcId}`}
            workAuthId={regionalCitizenship.id}
            editing={editingCitizenshipIds.includes(regionalCitizenship.rcId)}
            allCountries={allCountries}
            toggleFieldSection={() => handleToggleEditing(regionalCitizenship)}
            citizenshipStatus={regionalCitizenship.citizenshipStatus}
            permitType={regionalCitizenship.permitType}
            countryName={regionalCitizenship.countryName}
            countryIso2Code={regionalCitizenship.countryIso2Code}
            candidateId={props.candidateId}
            isDeletingCitizenship={isDeletingCitizenship}
            deleteCitizenship={deleteCitizenship}
            isUpdatingCitizenship={isUpdatingCitizenship}
            validateCountry={validateCountry}
            control={control}
            errors={errors}
            submitForm={submitUpdateForm}
            citizenshipId={regionalCitizenship.id}
          />
        </For>
        <Choose>
          <When condition={newCitizenship}>
            <WorkAuthFields
              key="new-work-auth"
              toggleFieldSection={handleToggleAdding}
              createCitizenship={createCitizenship}
              candidateId={props.candidateId}
              allCountries={allCountries}
              closeForm={() => setNewCitizenship(false)}
              selectedCountries={selectedCountries}
              isCreatingCitizenship={isCreatingCitizenships}
              validateCountry={validateCountry}
              control={control}
              errors={errors}
              index={controlledFields.length}
              isNewWorkAuth
            />
          </When>
          <Otherwise>
            <Button onClick={handleToggleAdding} theme={props.theme} size="medium" variant="outlined" label="Add work authorisation" />
          </Otherwise>
        </Choose>
      </WorkAuthList>

      <CardFooterNav
        submitDisabled={!!errors.citizenships || !!errors.regionalCitizenships}
        navigateBack={() => history.push(previousOnboardingUrl(location.pathname))}
        loading={isCreatingCitizenships && !isLoadingSubmitNewWorkAuth}
        theme="primary"
      />
    </form>
  )
}

CitizenshipStatusByCountryForm.propTypes = {
  regionalCitizenships: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      citizenshipStatus: PropTypes.string,
      citizenshipCountry: PropTypes.string,
    }),
  ),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  reduxChangeUrl: PropTypes.func.isRequired,
  nextUrl: PropTypes.string.isRequired,
  candidateId: PropTypes.string,
  theme: PropTypes.string,
  preferredCountries: PropTypes.arrayOf(PropTypes.string),
}

export default CitizenshipStatusByCountryForm
